import { jsonAssignInlineVars } from '@vuddy/utils';
import clsx from 'clsx';
import { isNil } from 'lodash-es';
import React, { ComponentType, createElement, Ref } from 'react';
import { Row } from '../flex/Row';
import { icon, theme } from '../theme/theme.css';
import * as styles from './icon.css';

export type PreLoadableComponent<T extends ComponentType<any>> = T & {
  preload: () => Promise<void>;
};

// https://github.com/ianschmitz/react-lazy-with-preload/blob/master/src/index.ts

export default function lazyWithPreload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): PreLoadableComponent<T> {
  const LazyComponent = React.lazy(factory);
  let factoryPromise: Promise<void> | undefined;
  let LoadedComponent: T | undefined;

  const Component = function (props: any) {
    return createElement(LoadedComponent ?? LazyComponent, props);
  } as any as PreLoadableComponent<T>;

  Component.preload = () => {
    if (!factoryPromise) {
      factoryPromise = factory().then(module => {
        LoadedComponent = module.default;
      });
    }

    return factoryPromise;
  };
  return Component;
}

const loadIcon = (iconFile: string) =>
  lazyWithPreload(() => import(`./icons/${iconFile}.svg`));

export const icons = {
  ALARM: loadIcon('alarm-24'),
  DARK_ALARM: loadIcon('dark-alarm'),

  ETC_APP_APPSTORE: loadIcon('etc-app-appstore'),
  ETC_APP_GOOGLEPLAY: loadIcon('etc-app-googleplay'),
  ETC_APPICON: loadIcon('etc_appicon'),
  QR_CODE_DOWNLOAD: loadIcon('qr-code-download'),
  ETC_ERRORGRAPHIC: loadIcon('etc-errorgraphic'),
  ETC_IMAGE_BLANK: loadIcon('etc-image-blank'),
  ETC_LOGO_APPLE: loadIcon('etc-logo-apple'),
  ETC_LOGO_GOOGLE: loadIcon('etc-logo-google'),
  ETC_LOGO_KAKAOPAY: loadIcon('etc-logo-kakaopay'),
  ETC_LOGO_NAVERPAY: loadIcon('etc-logo-naverpay'),
  ETC_LOGO_PAYCO: loadIcon('etc-logo-payco'),
  ETC_LOGO_SAMSUNGPAY: loadIcon('etc-logo-samsungpay'),
  ETC_LOGO_TOSSPAY: loadIcon('etc-logo-tosspay'),
  ETC_LOGO_VISA: loadIcon('etc-logo-visa'),
  ETC_LOGO_VISA_DISABLED: loadIcon('etc-logo-visa-disabled'),
  ETC_LOGO_MASTER: loadIcon('etc-logo-master'),
  ETC_LOGO_MASTER_DISABLED: loadIcon('etc-logo-master-disabled'),
  ETC_LOGO_JCB: loadIcon('etc-logo-jcb'),
  ETC_LOGO_JCB_DISABLED: loadIcon('etc-logo-jcb-disabled'),
  ETC_LOGO_VUDDY: loadIcon('etc-logo-vuddy'),
  ETC_LOGO_NAVER: loadIcon('etc-logo-naver'),
  ETC_PROFILE_BLANK: loadIcon('etc-profile-blank'),
  ETC_VUDDYWORDMARK: loadIcon('etc-vuddywordmark'),
  ETC_LOGOGRAPHIC: loadIcon('etc-logographic'),
  ETC_POINT: loadIcon('etc_point'),
  ETC_GACHA_SSS: loadIcon('etc-gacha-sss'),
  ETC_GACHA_SS: loadIcon('etc-gacha-ss'),
  ETC_GACHA_S: loadIcon('etc-gacha-s'),
  ETC_GACHA_A: loadIcon('etc-gacha-a'),
  ETC_GACHA_B: loadIcon('etc-gacha-b'),
  ETC_GACHA_C: loadIcon('etc-gacha-c'),
  ETC_GACHA_UNVEIL: loadIcon('etc-gacha-unveil'),

  FILL_BOX: loadIcon('fill-box'),
  FILL_CAMERA: loadIcon('fill-camera'),
  FILL_CHAT: loadIcon('fill-chat'),
  FILL_CHECK_CIRCLE: loadIcon('fill-check-circle'),
  FILL_CLOSE_CIRCLE: loadIcon('fill-close-circle'),
  FILL_CROWN: loadIcon('fill-crown'),
  FILL_VUDDYCARD: loadIcon('fill-vuddycard'),
  FILL_EDIT: loadIcon('fill-edit'),
  FILL_EYE: loadIcon('fill-eye'),
  FILL_HOME: loadIcon('fill-home'),
  FILL_HOUSE: loadIcon('fill-house'),
  FILL_INFORMATION_CIRCLE: loadIcon('fill-information-circle'),
  FILL_LIKE: loadIcon('fill-like'),
  FILL_MIC: loadIcon('fill-mic'),
  FILL_LOGO_VUDDY: loadIcon('fill-logo-vuddy'),
  FILL_MINUS_CIRCLE: loadIcon('fill-minus-circle'),
  FILL_NEW: loadIcon('fill-new'),
  FILL_NOTIFICATION: loadIcon('fill-notification'),
  FILL_PAUSE: loadIcon('fill-pause'),
  FILL_PERSON: loadIcon('fill-person'),
  FILL_PHOTO: loadIcon('fill-photo'),
  FILL_PIN: loadIcon('fill-pin'),
  FILL_PLAY: loadIcon('fill-play'),
  FILL_PLUS_CIRCLE: loadIcon('fill-plus-circle'),
  FILL_POST: loadIcon('fill-post'),
  FILL_PROMOTION_CODE: loadIcon('fill-promotion-code'),
  FILL_RANK: loadIcon('fill-rank'),
  FILL_SEND: loadIcon('fill-send'),
  FILL_SHOP: loadIcon('fill-shop'),
  FILL_SKIP: loadIcon('fill-skip'),
  FILL_SORTING: loadIcon('fill-sorting'),
  FILL_TRASH: loadIcon('fill-trash'),
  FILL_TRIANGLE_DOWN: loadIcon('fill-triangle-down'),
  FILL_TRIANGLE_LEFT: loadIcon('fill-triangle-left'),
  FILL_TRIANGLE_RIGHT: loadIcon('fill-triangle-right'),
  FILL_TRIANGLE_UP: loadIcon('fill-triangle-up'),
  FILL_VERIFIED: loadIcon('fill-verified'),
  FILL_VIDEO: loadIcon('fill-video'),
  FILL_WARNING: loadIcon('fill-warning'),
  FILL_ALARM: loadIcon('fill-alarm'),
  FILL_TICKET: loadIcon('fill-ticket'),
  FILL_COLLECTION: loadIcon('fill-collection'),
  FILL_CLASS: loadIcon('fill-class'),
  FILL_TIME: loadIcon('fill-time'),
  FILL_REWARDS: loadIcon('fill-rewards'),
  FILL_FAN_CREATOR: loadIcon('fill-fan-creator'),

  LINK_LOGO_DEFAULT: loadIcon('link-logo-default'),
  LINK_LOGO_CHZZK: loadIcon('link-logo-chzzk'),
  LINK_LOGO_X: loadIcon('link-logo-x'),
  LINK_LOGO_INSTAGRAM: loadIcon('link-logo-instagram'),
  LINK_LOGO_AFREECATV: loadIcon('link-logo-afreecatv'),
  LINK_LOGO_DISCORD: loadIcon('link-logo-discord'),
  LINK_LOGO_YOUTUBE: loadIcon('link-logo-youtube'),
  LINK_LOGO_NAVERCAFE: loadIcon('link-logo-navercafe'),

  OUTLINE_ADDRESS: loadIcon('outline-address'),
  OUTLINE_ARROW_DOWN: loadIcon('outline-arrow-down'),
  OUTLINE_ARROW_IOSBACK: loadIcon('outline-arrow-iosback'),
  OUTLINE_ARROW_LEFT: loadIcon('outline-arrow-left'),
  OUTLINE_ARROW_RIGHT: loadIcon('outline-arrow-right'),
  OUTLINE_ARROW_UP: loadIcon('outline-arrow-up'),
  OUTLINE_BOX: loadIcon('outline-box'),
  OUTLINE_CALENDAR: loadIcon('outline-calendar'),
  OUTLINE_CAMERA: loadIcon('outline-camera'),
  OUTLINE_CARD: loadIcon('outline-card'),
  OUTLINE_CART: loadIcon('outline-cart'),
  OUTLINE_CLASS: loadIcon('outline-class'),
  OUTLINE_DOCUMENT: loadIcon('outline-document'),
  OUTLINE_CHANNEL: loadIcon('outline-channel'),
  OUTLINE_CHECK_CIRCLE: loadIcon('outline-check-circle'),
  OUTLINE_CHECK: loadIcon('outline-check'),
  OUTLINE_CHEVRON_DOWN: loadIcon('outline-chevron-down'),
  OUTLINE_CHEVRON_LEFT: loadIcon('outline-chevron-left'),
  OUTLINE_CHEVRON_RIGHT: loadIcon('outline-chevron-right'),
  OUTLINE_CHEVRON_UP: loadIcon('outline-chevron-up'),
  OUTLINE_CHEVRON_UPDOWN: loadIcon('outline-chevron-updown'),
  OUTLINE_CLIPBOARD: loadIcon('outline-clipboard'),
  OUTLINE_CLOSE: loadIcon('outline-close'),
  OUTLINE_COPY: loadIcon('outline-copy'),
  OUTLINE_DASHBOARD: loadIcon('outline-dashboard'),
  OUTLINE_DOWNLOAD: loadIcon('outline-download'),
  OUTLINE_EDIT: loadIcon('outline-edit'),
  OUTLINE_EXCHANGE: loadIcon('outline_exchange'),
  OUTLINE_ELLIPSIS_HORIZON: loadIcon('outline-ellipsis-horizon'),
  OUTLINE_ELLIPSIS_VERTICAL: loadIcon('outline-ellipsis-vertical'),
  OUTLINE_EMAIL: loadIcon('outline-email'),
  OUTLINE_EXPEND: loadIcon('outline-expend'),
  OUTLINE_HOUSE: loadIcon('outline-house'),
  OUTLINE_FAQ: loadIcon('outline-faq'),
  OUTLINE_FOLLOWER: loadIcon('outline-follower'),
  OUTLINE_GACHA: loadIcon('outline_gacha'),
  OUTLINE_HEADPHONE: loadIcon('outline-headphone'),
  OUTLINE_INFORMATION: loadIcon('outline-information'),
  OUTLINE_LIKE: loadIcon('outline-like'),
  OUTLINE_LINK: loadIcon('outline-link'),
  OUTLINE_LOCK: loadIcon('outline-lock'),
  OUTLINE_LOWRANK: loadIcon('outline-lowrank'),
  OUTLINE_MESSAGE: loadIcon('outline-message'),
  OUTLINE_MENU_CHAT: loadIcon('outline-menu-chat'),
  OUTLINE_MONEY: loadIcon('outline-money'),
  OUTLINE_MUTE: loadIcon('outline-mute'),
  OUTLINE_NOPE: loadIcon('outline-nope'),
  OUTLINE_NOTIFICATION: loadIcon('outline-notification'),
  OUTLINE_MENU: loadIcon('outline-menu'),
  OUTLINE_OUTLINK: loadIcon('outline-outlink'),
  OUTLINE_PERSON: loadIcon('outline-person'),
  OUTLINE_PHOTO: loadIcon('outline-photo'),
  OUTLINE_PIN: loadIcon('outline-pin'),
  OUTLINE_PLUS: loadIcon('outline-plus'),
  OUTLINE_MINUS: loadIcon('outline-minus'),
  OUTLINE_POST: loadIcon('outline-post'),
  OUTLINE_REDUCE: loadIcon('outline-reduce'),
  OUTLINE_RELOAD: loadIcon('outline-reload'),
  OUTLINE_REORDER: loadIcon('outline-reorder'),
  OUTLINE_SEARCH: loadIcon('outline-search'),
  OUTLINE_SETTING: loadIcon('outline-setting'),
  OUTLINE_SHARE: loadIcon('outline-share'),
  OUTLINE_SHOP: loadIcon('outline-shop'),
  OUTLINE_SIREN: loadIcon('outline-siren'),
  OUTLINE_SPEAKER: loadIcon('outline-speaker'),
  OUTLINE_STORE: loadIcon('outline-store'),
  OUTLINE_TEMPLATE: loadIcon('outline-template'),
  OUTLINE_TICKET: loadIcon('outline-ticket'),
  OUTLINE_TIMER: loadIcon('outline-timer'),
  OUTLINE_TOOL: loadIcon('outline-tool'),
  OUTLINE_TRASH: loadIcon('outline-trash'),
  OUTLINE_VERIFIED: loadIcon('outline-verified'),
  OUTLINE_VIDEO: loadIcon('outline-video'),
  OUTLINE_WARNING: loadIcon('outline-warning'),
  OUTLINE_BROADCAST: loadIcon('outline-broadcast'),
  OUTLINE_COLLECTION: loadIcon('outline_collection'),
  OUTLINE_GRID: loadIcon('outline_grid'),
  OUTLINE_EMPTY_RADIO: loadIcon('outline-empty-radio'),
  OUTLINE_FAN_CREATOR: loadIcon('outline-fan-creator'),

  SCROLL_DOWN: loadIcon('scrolldown'),

  TITLE_DECO_L: loadIcon('title_deco_L'),
  TITLE_DECO_R: loadIcon('title_deco_R'),
  TOPBAR_LEAD_HOME: loadIcon('topbar-lead-home'),

  // 채팅 위젯
  CHAT_WIDGET_SOOP: loadIcon('chat-widget-soop'),
  CHAT_WIDGET_CHZZK: loadIcon('chat-widget-chzzk'),
  CHAT_WIDGET_YOUTUBE: loadIcon('chat-widget-youtube'),
  CHAT_WIDGET_VUDDY: loadIcon('chat-widget-vuddy'),

  ICON_RIGHT_ARROW: loadIcon('icon-right-arrow'),
  ICON_LEFT_ARROW: loadIcon('icon-left-arrow'),
  ICON_UP_ARROW: loadIcon('icon-up-arrow'),
  ICON_DOWN_ARROW: loadIcon('icon-down-arrow'),

  ICON_TABLE: loadIcon('icon-table'),
  ICON_GRID: loadIcon('icon-grid'),
} as const;

export type IconName = keyof typeof icons;
export type IconColor = keyof typeof icon;

const iconPreLoadedFlagEntries: Array<[IconName, boolean]> = Array.from(
  Object.keys(icons)
).map(iconName => [iconName as IconName, false] as const);

const iconPreLoadedMap = Object.fromEntries(iconPreLoadedFlagEntries);

const preloadIcons = (preloadIcons: IconName[]) => {
  preloadIcons
    .filter(iconName => !iconPreLoadedMap[iconName])
    .forEach(iconName => {
      iconPreLoadedMap[iconName] = true;
      icons[iconName].preload();
    });
};

export interface IIconProps {
  name: IconName;
  color?: IconColor;
  colorCode?: string;
  opacity?: number;
  preloadIconNames?: IconName[];
  rotate?: boolean;
  rotateTime?: number;
  uniqueKey?: string;
  size?: string;
  width?: string;
  height?: string;
  className?: string;
  ref?: Ref<HTMLElement>;
}

export const Icon = ({
  preloadIconNames = [],
  name,
  color,
  colorCode,
  opacity,
  rotate = false,
  rotateTime = 300,
  uniqueKey,
  size,
  width = size,
  height = size,
  className,
  ref: forwardedRef,
  ...props
}: IIconProps) => {
  const IconComponent = icons[name];

  const iconWidth = width ?? '24px';
  const iconHeight = height ?? '24px';

  preloadIcons(preloadIconNames);

  return (
    <Row
      ref={forwardedRef}
      className={clsx(styles.wrapper, className)}
      style={jsonAssignInlineVars({
        [styles.widthVar]: iconWidth,
        [styles.heightVar]: iconHeight,
        [styles.animationVar]:
          rotate && rotateTime
            ? `${styles.rotateKeyframes} ${rotateTime}ms infinite`
            : undefined,
      })}>
      <IconComponent
        key={uniqueKey}
        color={(colorCode ?? isNil(color)) ? undefined : theme.icon[color]}
        opacity={opacity}
        flex="none"
        title={[name, color ?? ''].join('-')}
        className={styles.icon}
        {...props}
      />
    </Row>
  );
};
